import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, useRoutes } from "react-router-dom";
import { ConfigProvider, Spin } from "antd";
import zhCN from "antd/lib/locale/zh_CN";

// 导入集中式配置
import { themeConfig } from "./config";
import { getRouteObjects } from "./routes";
import { initializeApp } from "./service/appInitializer";
import DebugFloatPanel from "./components/DebugFloatPanel";
import { shouldShowDebugFloat } from "./utils/debugFloatEnabled";
import { ErrorBoundary } from "@ms/shared-components";
import { clearChunkReloadAttempts } from "./utils/chunkLoadRecovery";

// 导入统一认证系统（暂时禁用）
// import { useAuthWithConfig } from "@ms/shared-auth";
// import { msIncomeAuthConfig } from "./config";

// 样式
import "./App.css";

/**
 * 路由组件 - 带认证保护
 */
const AppRoutes: React.FC = () => {
  const routeObjects = React.useMemo(() => {
    return getRouteObjects();
  }, []);

  const routes = useRoutes(routeObjects);
  return routes;
};

/**
 * 应用主组件
 */
const App: React.FC = () => {
  const [initialized, setInitialized] = useState(false);

  // 使用认证 Hook（暂时禁用）
  // const { isAuthenticated, loading: authLoading } = useAuthWithConfig(msIncomeAuthConfig);

  useEffect(() => {
    // 过滤图表相关的警告输出
    const originalWarn = console.warn;
    console.warn = (...args: any[]) => {
      const message = args.join(' ');
      // 过滤掉关于"系列不存在"和"图例数据"的警告
      if (
        message.includes('series not exists') ||
        message.includes('Legend data should be same') ||
        message.includes('系列不存在') ||
        message.includes('图例数据应与系列名称') ||
        message.includes('图例数据应与数据名称')
      ) {
        return; // 不输出这些警告
      }
      // 其他警告正常输出
      originalWarn.apply(console, args);
    };

    const init = async () => {
      try {
        await initializeApp();
        setInitialized(true);
      } catch (error) {
        console.error("应用初始化失败:", error);
        setInitialized(true); // 即使失败也继续，避免卡住
      }
    };

    init();

    // 清理函数：恢复原始 console.warn
    return () => {
      console.warn = originalWarn;
    };
  }, []);

  // 应用成功进入主界面后清零拆包重试计数，便于下次发版再次自动恢复
  useEffect(() => {
    if (initialized) clearChunkReloadAttempts();
  }, [initialized]);

  return (
    <ConfigProvider locale={zhCN} theme={themeConfig}>
      <div
        style={{
          height: "100%",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Router
          {...({
            future: {
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            },
          } as any)}
        >
          {!initialized ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh",
                gap: 16,
              }}
            >
              <Spin size="large" />
              <span style={{ color: "#666" }}>加载中...</span>
            </div>
          ) : (
            <ErrorBoundary
              renderFallback={({ error }) => (
                <div
                  style={{
                    padding: 32,
                    maxWidth: 640,
                    margin: "48px auto",
                    color: "#333",
                  }}
                >
                  <h2 style={{ marginBottom: 16, color: "#cf1322", textAlign: "center" }}>
                    页面渲染异常
                  </h2>
                  <p style={{ marginBottom: 12, color: "#666", lineHeight: 1.6 }}>
                    下方为具体错误信息，便于排查。若刚发版，也可先尝试<strong>重新加载</strong>或强制刷新（Windows:
                    Ctrl+F5，Mac: Cmd+Shift+R）。
                  </p>
                  <pre
                    style={{
                      padding: 16,
                      background: "#fafafa",
                      border: "1px solid #f0f0f0",
                      borderRadius: 8,
                      fontSize: 13,
                      lineHeight: 1.5,
                      overflow: "auto",
                      maxHeight: 280,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {error.message}
                    {error.stack ? `\n\n${error.stack}` : ""}
                  </pre>
                  <div style={{ marginTop: 20, textAlign: "center" }}>
                    <button
                      type="button"
                      onClick={() => globalThis.location.reload()}
                      style={{
                        padding: "8px 20px",
                        background: "#1890ff",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        cursor: "pointer",
                        fontSize: 14,
                      }}
                    >
                      重新加载
                    </button>
                  </div>
                </div>
              )}
            >
              <AppRoutes />
            </ErrorBoundary>
          )}
          {shouldShowDebugFloat() ? <DebugFloatPanel /> : null}
        </Router>
      </div>
    </ConfigProvider>
  );
};

export default App;
